import { useEffect, useState, FC, useRef } from 'react'
import { EChart } from 'src/charts/EChart'
import { kFormatter } from 'src/utils/math'
import en from 'src/constants/en'
import { COLORS } from 'src/constants/colors'
import hexToRgbA from 'src/utils/hexToRgba'
import { useGetInformationTooltipsQuery } from 'src/reactQuery/useGetInformationTooltips'
import { AnalyticsFetchDailyTrendsResponse } from 'src/services/__generated__/api'
import { SectionTitle } from 'src/components/SectionTitle'
import { TitleColor } from 'src/components/SectionTitle/enums/titleColor.enum'
import { ReactComponent as AnnouncementIcon } from 'src/assets/svg/announcement.svg'
import { Skeleton } from 'src/components/Skeleton'
import styles from './dailyTrendsSection.module.scss'

// Real dimensions taken from dailyTrendsSection.module.scss: the mentions
// value is 20px/24px and the footer dates are 14px/16.41px. The chart itself
// lives in a fixed 336px-tall .chartContainer, so it is deliberately left
// alone — it cannot shift, and a skeleton there would be decoration only.
const MENTIONS_VALUE_HEIGHT = 24
const MENTIONS_VALUE_WIDTH = 72
const FOOTER_DATE_HEIGHT = 16
const FOOTER_DATE_WIDTH = 88

interface DailyTrendsSectionProps {
  chartData?: AnalyticsFetchDailyTrendsResponse
  isLoading?: boolean
  showTotalMentions?: boolean
}

export const DailyTrendsSection: FC<DailyTrendsSectionProps> = ({
  chartData,
  isLoading,
  showTotalMentions = false,
}) => {
  const { data: informationTooltips } = useGetInformationTooltipsQuery()

  const containerRef = useRef<HTMLDivElement>(null)

  const [options, setOptions] = useState<any>(null)

  // Calculate total mentions from all data points
  const totalMentions =
    chartData?.total?.reduce((sum, val) => sum + val, 0) || 0

  // No dates = nothing to plot. Render a centered empty state instead of an
  // empty full-height chart grid (which leaves an ugly blank purple block).
  const hasData = Boolean(chartData?.dates?.length)

  // Skeletons are for the FIRST load only — a refetch (e.g. a new date range)
  // keeps the previous totals and dates on screen rather than flashing them
  // back to placeholders. Without this the header reports a confident "0"
  // total mentions before any data has arrived.
  const isInitialLoading = !!isLoading && !chartData

  useEffect(() => {
    const getOptions = () => {
      const data = {
        backgroundColor: COLORS.malibu,
        grid: {
          height: '220',
          left: 'center',
          right: 'center',
          top: '70',
          width: '92%',
        },
        legend: {
          align: 'left',
          data: [
            {
              icon: 'circle',
              itemStyle: {
                borderColor: COLORS.malibu,
                borderWidth: 5,
                color: COLORS.darkBlue,
              },
              name: en.positive,
            },
            {
              icon: 'circle',
              itemStyle: {
                borderColor: COLORS.malibu,
                borderWidth: 5,
                color: COLORS.burningOrange,
              },
              name: en.negative,
              size: 8,
            },
            {
              icon: 'circle',
              itemStyle: {
                borderColor: COLORS.malibu,
                borderWidth: 5,
                color: hexToRgbA(COLORS.alto, 0.2),
                opacity: 1,
              },
              name: en.total,
            },
          ],
          itemGap: 32,
          left: 0,
          selectedMode: false,
          show: true,
          textStyle: {
            color: '#fff',
          },
          top: 0,
          useHTML: true,
        },
        series: [
          {
            color: '#0A12D6',
            data: chartData?.positives,
            emphasis: {
              focus: 'series',
            },
            itemStyle: {
              borderColor: 'rgba(255, 255, 255, 0.4)',
              borderWidth: 4,
              color: '#0A12D6',
            },
            lineStyle: {
              color: '#0A12D6',
              width: 1,
            },
            name: en.positive,
            showAllSymbol: false,
            showSymbol: true,
            smooth: false,
            symbol: 'circle',
            symbolSize: 10,
            type: 'line',
          },
          {
            data: chartData?.negatives,
            emphasis: {
              focus: 'series',
            },
            itemStyle: {
              borderColor: 'rgba(255, 255, 255, 0.4)',
              borderWidth: 4,
              color: '#FF6833',
            },
            lineStyle: {
              color: '#FF6833',
              width: 1,
            },
            name: en.negative,
            showAllSymbol: false,
            showSymbol: true,
            smooth: false,
            symbol: 'circle',
            symbolSize: 10,
            type: 'line',
          },
          {
            barGap: '-100%',
            barWidth: 24,
            data: chartData?.total,
            itemStyle: {
              borderRadius: [6, 6, 0, 0],
              color: '#D7D7D7',
              opacity: 0.2,
            },
            name: en.total,
            tooltip: {
              position: 'right',
            },
            type: 'bar',
            z: -12,
          },
        ],
        tooltip: {
          axisPointer: {
            lineStyle: {
              color: '#fff',
              opacity: '0.21',
            },
            type: 'line',
          },
          cursor: 'default',
          enterable: true,
          extraCssText: 'user-select: text;',
          show: true,
          trigger: 'axis',
          triggerOn: 'mousemove',
        },
        xAxis: {
          axisLabel: {
            show: false,
          },
          axisLine: {
            lineStyle: {
              color: '#fff',
              opacity: '0.1',
            },
          },
          axisPointer: {
            lineStyle: {
              type: 'solid',
            },
            show: true,
            triggerTooltip: false,
          },
          axisTick: {
            show: false,
          },
          boundaryGap: true,
          data: chartData?.dates,
        },
        yAxis: [
          {
            axisLabel: {
              formatter(value: number) {
                return kFormatter(value)
              },
              opacity: '0.47',
            },
            axisLine: {
              lineStyle: {
                color: '#fff',
              },
            },
            position: 'left',
            splitLine: {
              lineStyle: {
                opacity: '0.1',
              },
              show: true,
            },
            type: 'value',
          },
          {
            axisLabel: {
              formatter(value: number) {
                return kFormatter(value)
              },
              opacity: '0.47',
            },
            axisLine: {
              lineStyle: {
                color: '#fff',
              },
            },
            position: 'right',
            splitLine: {
              show: false,
            },
            type: 'value',
          },
        ],
      }

      setOptions(data)
    }

    if (chartData) {
      getOptions()
    }
  }, [chartData])

  function getFormattedDate(date: string | undefined) {
    if (date) {
      const formatter = new Intl.DateTimeFormat([], {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
      const [month, , day, , year] = formatter.formatToParts(new Date(date))
      return `${year.value} ${month.value} ${day.value}`
    }
    return ''
  }

  return (
    <div className={styles.dailyTrendsSection}>
      {showTotalMentions ? (
        <div className={styles.header}>
          <SectionTitle
            isLoading={isLoading}
            title={en.dailyTrends}
            titleColor={TitleColor.WHITE}
            tooltipContent={informationTooltips?.dailyTrends}
          />

          <div className={styles.totalMentionsCard}>
            <AnnouncementIcon className={styles.mentionsIcon} />
            <div className={styles.mentionsContent}>
              <span className={styles.mentionsLabel}>Total Mentions</span>
              {isInitialLoading ? (
                <Skeleton
                  height={MENTIONS_VALUE_HEIGHT}
                  radius={4}
                  width={MENTIONS_VALUE_WIDTH}
                />
              ) : (
                <span className={styles.mentionsValue}>
                  {totalMentions.toLocaleString()}
                </span>
              )}
            </div>
          </div>
        </div>
      ) : (
        <SectionTitle
          isLoading={isLoading}
          title={en.dailyTrends}
          titleColor={TitleColor.WHITE}
          tooltipContent={informationTooltips?.dailyTrends}
        />
      )}

      <div className={styles.chartContainer} ref={containerRef}>
        {hasData && options ? (
          <EChart options={options} width={containerRef.current?.offsetWidth} />
        ) : !isLoading ? (
          <div className={styles.emptyState}>{en.notEnoughDataToDisplay}</div>
        ) : null}
      </div>

      <div className={styles.footer}>
        {isInitialLoading ? (
          <>
            <Skeleton
              height={FOOTER_DATE_HEIGHT}
              radius={4}
              width={FOOTER_DATE_WIDTH}
            />
            <Skeleton
              height={FOOTER_DATE_HEIGHT}
              radius={4}
              width={FOOTER_DATE_WIDTH}
            />
          </>
        ) : (
          <>
            <span>{getFormattedDate(chartData?.dates?.[0])}</span>

            {!!chartData?.dates?.length && (
              <span>
                {getFormattedDate(chartData?.dates[chartData.dates.length - 1])}
              </span>
            )}
          </>
        )}
      </div>
    </div>
  )
}
